<template>
  <div class="activity-stage" :class="{ isMobileView: isMobile }">
    <div class="top-to-bottom-reveal-container block-container">
      <div
        v-for="block of currentRound.blocks"
        class="block"
        :id="`block-${block.id}`"
        :key="block.id"
        :style="{ height: `${100 / currentRound.blocks.length}%` }"
      >
        <div class="block-paragraph" v-for="step of block.steps" :key="step.id">
          <blinking-cursor-text
            :text="currentRound.steps.find(({ id }) => id === step.id).answer"
            :text-step="currentRound.steps.findIndex(({ id }) => id === step.id)"
          >
          </blinking-cursor-text>
        </div>
      </div>
    </div>

    <div id="img-overlay">
      <div id="img-container">
        <img id="img" :src="getImageSrc()" />
      </div>
    </div>

    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import { TweenMax, Back } from 'gsap';
import BlinkingCursorText from '../components/text-displays/BlinkingCursorText.vue';

export default {
  mixins: [AssetLoader, ActivityManager, TypingActivityHelper, ErrorLogger],

  components: {
    BlinkingCursorText
  },

  data() {
    return {
      activityName: 'TopBottomPanelReveal'
    };
  },

  methods: {
    getImageSrc() {
      const src = this.currentRound.contentImage?.image.src || '';

      return src;
    },

    async loadActivity() {
      const activityState = this.getActivityState();
      this.createRounds(activityState);
      await this.loadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          delay: 4,
          voice: `${this.voiceInstructionsPrefix}/typetosee-fa-1.mp3`
        }
      };
    },

    fadeInTypeToSee() {
      const elem = document.querySelector('.top-to-bottom-reveal-container');
      TweenMax.to(elem, 1, { css: { opacity: 1 } });
    },

    getKeyboardConfig() {
      const { keyboardType } = this.activityData.keyboard;

      return {
        keyboardType,
        keys: undefined,
        hideNumberRow: false
      };
    },

    async loadImages() {
      this.rounds.forEach(({ contentImage }) => {
        this.queueImage(contentImage.image.src);
      });

      return this.downloadImages();
    },

    setContainerHeight() {
      const keyboard = document.querySelector('.phoenix-keyboard-wrapper');
      const mainContainer = document.querySelector('.main-container');
      const blockContainer = document.querySelector('.block-container');

      blockContainer.style.height = `calc(${
        mainContainer.clientHeight
      }px - ${keyboard.clientHeight + 10}px)`;
    },

    setImageHeight() {
      const elem = document.getElementById('img');
      if (!elem) return;

      const mainContainer = document.querySelector('.main-container');
      const keyboard = document.querySelector('.phoenix-keyboard-wrapper');

      elem.style.height = `calc(${mainContainer.clientHeight}px - ${keyboard.clientHeight + 10}px)`;
      elem.style.width = 'auto';
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(() => {
        this.setContainerHeight();
        this.setImageHeight();
      });
      this.$nextTick(this.fadeInTypeToSee);
    },

    getCurrentBlock() {
      return this.currentRound.blocks.find(block =>
        block.steps.some(step => step.id === this.currentStep.id)
      );
    },

    getNextBlock() {
      const currentBlockIndex = this.currentRound.blocks.findIndex(block =>
        block.steps.some(step => step.id === this.currentStep.id)
      );

      return this.currentRound.blocks[currentBlockIndex + 1];
    },

    isLastStepInBlock(block, step) {
      return block.steps[block.steps.length - 1].id === step.id;
    },

    handleStepEnd() {
      this.sndBell();
      this.completeStep();

      const currentBlock = this.getCurrentBlock();

      if (this.isLastStepInBlock(currentBlock, this.currentStep)) {
        const nextBlock = this.getNextBlock();

        const currentElem = document.getElementById(`block-${currentBlock.id}`);
        const nextElem = nextBlock ? document.getElementById(`block-${nextBlock.id}`) : undefined;

        const posY = (nextElem ? nextElem.offsetTop : currentElem.offsetTop) - 5;
        const height = this.isLastStepInRound
          ? document.querySelector('.activity-stage').scrollHeight
          : posY;

        TweenMax.to(`#block-${currentBlock.id}`, 0.5, { css: { opacity: 0 } });
        TweenMax.to('#img-overlay', 2, { height, ease: Back.easeInOut });
      }

      if (this.isEndOfActivity) {
        TweenMax.delayedCall(1, this.hideKeyboard);
        TweenMax.delayedCall(1, this.playCongrats);
        TweenMax.delayedCall(4, () => this.completeActivity(false));
      } else {
        this.setCurrentStep();
      }
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.$nextTick(() => this.handleStepEnd());
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.top-to-bottom-reveal-container {
  opacity: 0;
  padding-bottom: 10px;
  padding-top: 5px;
}

.block-spacer {
  margin-top: 70px;
}

.mobile-block-spacer {
  margin-top: 10px;
}

.block-container {
  display: flex;
  flex-direction: column;
  height: calc(100% - 384px);
  padding-left: 20px;
}

.block-paragraph p {
  font-size: 18px;
}

.block {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.activity-stage {
  height: 768px;
  position: relative;
}

.activity-stage.isMobileView {
  height: 100vh;
}

#img-overlay {
  top: 0;
  left: 0;
  height: 0;
  width: 100%;
  overflow: hidden;
  position: absolute;
  border-bottom: 4px solid #000;
  z-index: 100;
}

#img-container {
  height: 100%;
  width: 100%;
  text-align: center;
}
</style>
